/*
 * File: Store.js
 * Project: mp-mobile-app
 *
 * Copyright (c) 2020 MacPractice, Inc. All rights reserved.
 *
 * Description: Static store to pesist login related data
 */
import PubSub from "pubsub-js";
import PatientId from "services/patients/store/PatientIdStore";
import ErrorStore from "services/login/ErrorStore";
import Preferences from "services/preferences/Store";
import { Instance } from "mobx-state-tree";

const DefaultMinutes = 15;

export interface JWT {
  exp: number;
  usergroupId: number;
  userId: number;
  clientTypeId: number;
  userUniqueId: string;
  db: string;
}

class Store {
  storageKeyName: string;
  /**
   * @param storageKeyNameName Name for the token in storage
   */
  constructor(storageKeyName: string) {
    this.storageKeyName = storageKeyName;
  }

  logoutMinutes(preferencesStore: Instance<typeof Preferences>) {
    const minutes = preferencesStore.get("autoLogout");
    return !minutes || minutes < 1 ? DefaultMinutes : minutes;
  }

  /**
   * Handles logging out. Destroys the JWT and passes in the logout message to the login form after logging out.
   * Then redirects to the login page.
   */
  logout = (errorMessage?: string) => {
    // App component will navigate to login page in response to this message
    PubSub.publish("LOGGING_OUT", true);

    this.destroy();
    this.removePatientId();
    if (errorMessage === "API error") {
      ErrorStore.setError(
        "API Error - Unauthorized Access, Please Log Back In"
      );
    } else if (errorMessage === "Timer expired") {
      ErrorStore.setError("Logged Out Due To Inactivity");
    } else {
      ErrorStore.setError("Logged Out Successfully");
    }
  };

  // removes the jwt token
  destroy = () => {
    ErrorStore.removeError();
    localStorage.removeItem(this.storageKeyName);
  };

  removePatientId = () => {
    PatientId.removeId();
  };

  /**
   * Store the jwt
   *
   * @return Payload of token
   */
  setJwt = (jwt: string): JWT => {
    localStorage.setItem(this.storageKeyName, jwt);
    return this.decode(jwt);
  };

  /**
   * Gets the jwt from storage
   */
  getJwt = (): string | null => {
    const token = localStorage.getItem(this.storageKeyName);
    if (token) return token;
    return null;
  };

  /**
   * Base 64 Decodes the token payload
   */
  decode = (token?: string): JWT => {
    if (!token) token = this.getJwt() ?? undefined;
    if (!token) throw new Error("Cannot decode null JWT");

    const parts = token.split(".");
    if (parts.length <= 1) {
      throw new Error("JWT was not formatted correctly");
    }

    try {
      return JSON.parse(atob(parts[1]));
    } catch (e) {
      throw new Error(`Token could not be split: ${e}`);
    }
  };
}

const LoginStore = new Store("mp-mobile-web-token");
export default LoginStore;
